import Phaser from 'phaser';

import * as ABCya from '@abcya/abcya_phaser';
import Region from '../Region';
import UIAssetKeys from '../UIAssetKeys';
import gameData, { AREAS, getFoundShapesInArea, getTotalFoundShapes, REGIONS } from '../gameData';
import BlueBuildingArea from './BlueBuildingArea';
import PeachBuildingArea from './PeachBuildingArea';
import TreeAndBenchArea from './TreeAndBenchArea';
import GreenBuildingArea from './GreenBuildingArea';
import SoundAssetKeys from '../SoundAssetKeys';

export default class DowntownRegion extends Region
{
    /**
     * Create the Downtown region screen
     * @param {Phaser.Scene} scene the owner scene
     */
    constructor(scene)
    {
        super(scene, REGIONS.DOWNTOWN);

        this.isLocked = gameData.region_unlock_requirements[REGIONS.DOWNTOWN] > getTotalFoundShapes();

        this.background = ABCya.Images.addImage(0, 0, UIAssetKeys._07_Downtown_Dwntwn_BG, this).setOrigin(0);

        this._addGraphics();
        this._createMenuButton(UIAssetKeys._Buttons_City_Btn_City_Menu3, UIAssetKeys._Buttons_City_Btn_City_Menu2, UIAssetKeys._Buttons_City_Btn_City_Menu1);
        this._createProgressButton(UIAssetKeys._Buttons_Btn_City_Prgrs3, UIAssetKeys._Buttons_Btn_City_Prgr2, UIAssetKeys._Buttons_Btn_City_Prgrs1);
        this._createInformation(UIAssetKeys._07_Downtown_HldingShp_Dntn, UIAssetKeys._03_Beach_HldingShp_Cmplt, 0xffffff, 0x2c2c89, 0x2b4270);
        this._createBackButton(UIAssetKeys._Buttons_City_Btn_City_Menu3, UIAssetKeys._Buttons_City_Btn_City_Back2, UIAssetKeys._Buttons_City_Btn_City_Back1);

        this.blueBuildingArea = new BlueBuildingArea(this);
        this.peachBuildingArea = new PeachBuildingArea(this);
        this.treeAndBenchArea = new TreeAndBenchArea(this);
        this.greenBuildingArea = new GreenBuildingArea(this);

        this._createEventListeners();

        this.blueBuildingProgress = this._createAreaProgressCounter(347, 154, UIAssetKeys._07_Downtown_HldingShp_Dntn, UIAssetKeys._03_Beach_HldingShp_Cmplt, 0x2c2c89, AREAS[REGIONS.DOWNTOWN].BLUE_BUILDING);
        this.peachBuildingProgress = this._createAreaProgressCounter(538, 60, UIAssetKeys._07_Downtown_HldingShp_Dntn, UIAssetKeys._03_Beach_HldingShp_Cmplt, 0x2c2c89, AREAS[REGIONS.DOWNTOWN].PEACH_BUILDING);
        this.treeAndBenchProgress = this._createAreaProgressCounter(500, 418, UIAssetKeys._07_Downtown_HldingShp_Dntn, UIAssetKeys._03_Beach_HldingShp_Cmplt, 0x2c2c89, AREAS[REGIONS.DOWNTOWN].TREE_AND_BENCH);
        this.greenBuildingProgress = this._createAreaProgressCounter(918, 93, UIAssetKeys._07_Downtown_HldingShp_Dntn, UIAssetKeys._03_Beach_HldingShp_Cmplt, 0x2c2c89, AREAS[REGIONS.DOWNTOWN].GREEN_BUILDING);

        this._createGreatWorkPopup();
        this._createRegionUnlockedPopup();

        this.jingleAudio = ABCya.Audio.createSound(this.scene);

        //this._addReference("DowntownRegionZoomedOut", "assets/reference_images/9 Get In Shape_Downtown_1-01.jpg");
        //this._addReference("DowntownRegionZoomedOut-AreaCounters", "assets/reference_images/9 Get In Shape_Downtown_2-01.jpg");
        //this._addReference("DowntownRegionZoomedIn-BlueBuilding", "assets/reference_images/9 Get In Shape_Downtown_3-01.jpg");
        //this._addReference("DowntownRegionZoomedIn-GreenBuilding", "assets/reference_images/9 Get In Shape_Downtown_6-01.jpg");
        //this._addReference("DowntownRegionZoomedIn-PeachBuilding", "assets/reference_images/9 Get In Shape_Downtown_5-01.jpg");
        //this._addReference("DowntownRegionZoomedIn-TreeAndBenchArea", "assets/reference_images/9 Get In Shape_Downtown_4-01.jpg");
        //ABCya.Debug.enableImagePlacerDebug(this.scene, this.background, this.ground);

        this.reset();
    }

    /**
     * Reset should be called when you want to set the state to a disabled/inactive state.
     */
    reset()
    {
        super.reset();

        this.blueBuilding.disableInteractive();
        this.peachBuilding.disableInteractive();
        this.greenBuilding.disableInteractive();
        this.treeAndBenchAreaButton.disableInteractive();

        this.blueBuildingArea.reset();
        this.peachBuildingArea.reset();
        this.treeAndBenchArea.reset();
        this.greenBuildingArea.reset();
    }

    /**
     * Prepare should be called before you want to prepare the state to become active.
     */
    prepare()
    {
        super.prepare();

        this.blueBuilding.disableInteractive();
        this.peachBuilding.disableInteractive();
        this.greenBuilding.disableInteractive();
        this.treeAndBenchAreaButton.disableInteractive();

        this.blueBuildingProgress.setAlpha(0);
        this.treeAndBenchProgress.setAlpha(0);
        this.greenBuildingProgress.setAlpha(0);
        this.peachBuildingProgress.setAlpha(0);

        this.updateRegionData();

        this.blueBuildingArea.reset();
        this.peachBuildingArea.reset();
        this.treeAndBenchArea.reset();
        this.greenBuildingArea.reset();
    }

    /**
     * Animates the states in.
     */
    async begin()
    {
        ABCya.Audio.playOneShot(SoundAssetKeys._city_drop_in, this.scene, 1, false, 0.5);

        let tweens = [
            { targets: this.background, duration: 750, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: 0 },
            { targets: this.ground, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.8], props: { y: this.ground.onScreenPosition.y }, offset: 0 },
            { targets: this.peachBuilding, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.peachBuilding.onScreenPosition.y }, offset: 300 },
            { targets: this.blueBuilding, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.blueBuilding.onScreenPosition.y }, offset: 400 },
            { targets: this.greenBuilding, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.greenBuilding.onScreenPosition.y }, offset: 500 },
            { targets: this.treeGrate, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.treeGrate.onScreenPosition.y }, offset: 550 },
            { targets: this.tree, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.tree.onScreenPosition.y }, offset: 600 },
            { targets: this.bench, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.bench.onScreenPosition.y }, offset: 700 },
            { targets: this.trashcan, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.trashcan.onScreenPosition.y }, offset: 650 },
            { targets: this.light0, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.light0.onScreenPosition.y }, offset: 700 },
            { targets: this.light1, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.light1.onScreenPosition.y }, offset: 575 },
            { targets: this.flowers[0], duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.flowers[0].onScreenPosition.y }, offset: 710 },
            { targets: this.flowers[1], duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.flowers[1].onScreenPosition.y }, offset: 720 },
            { targets: this.flowers[2], duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.flowers[2].onScreenPosition.y }, offset: 730 },
            { targets: this.flowers[3], duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.flowers[3].onScreenPosition.y }, offset: 740 },
            { targets: this.flowers[4], duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.flowers[4].onScreenPosition.y }, offset: 750 },
            { targets: this.flowers[5], duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.flowers[5].onScreenPosition.y }, offset: 780 },
            { targets: this.flowers[6], duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.flowers[6].onScreenPosition.y }, offset: 790 },
            { targets: this.flowers[7], duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.flowers[7].onScreenPosition.y }, offset: 800 },
            { targets: this.flowers[8], duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.flowers[8].onScreenPosition.y }, offset: 810 },
            { targets: this.flowers[9], duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.flowers[9].onScreenPosition.y }, offset: 820 },
            { targets: this.flowers[10], duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.flowers[10].onScreenPosition.y }, offset: 830 },
            { targets: this.flowers[11], duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.flowers[11].onScreenPosition.y }, offset: 840 },

            { targets: this.shadows, duration: 750, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: 1200 },
            { targets: this.informationContainer, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { x: this.informationContainer.onScreenPosition.x }, offset: 1200 },
            { targets: this.menuButton, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { scale: 1 }, offset: 1300 },
            { targets: this.progressButton, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { scale: 1 }, offset: 1400 },
        ];

        this._createAreaProgressFadeIn(tweens, 1500);

        let timeline = this.scene.tweens.timeline({
            tweens: tweens,
            onComplete: () =>
            {
                this.menuButton.setInteractive();
                this.progressButton.setInteractive();

                this.blueBuilding.setInteractive();
                this.peachBuilding.setInteractive();
                this.greenBuilding.setInteractive();
                this.treeAndBenchAreaButton.setInteractive();

                this._createIdleAnimations();

                ABCya.Audio.playSound(SoundAssetKeys._city_jingle, this.jingleAudio, 1, true);
            }
        });

        let waitForTimeline = new Promise((resolve) =>
        {
            timeline.on(Phaser.Tweens.Events.TIMELINE_COMPLETE, () => resolve());
        })
        await waitForTimeline;
    }

    /**
     * Animates the state out.
     */
    async leave()
    {
        this.menuButton.disableInteractive();
        this.progressButton.disableInteractive();

        this.blueBuilding.disableInteractive();
        this.peachBuilding.disableInteractive();
        this.greenBuilding.disableInteractive();
        this.treeAndBenchAreaButton.disableInteractive();

        this._stopIdleAnimations();

        ABCya.Audio.playOneShot(SoundAssetKeys._city_jump_up, this.scene, 1, false, 0.75);

        let tweens = [
            { targets: this.jingleAudio, duration: 750, ease: Phaser.Math.Easing.Quadratic.Out, props: { volume: 0 }, offset: 0, onComplete: () => this.jingleAudio.stop() },
            { targets: this.background, duration: 750, ease: Phaser.Math.Easing.Quadratic.In, props: { alpha: 0 }, offset: 1500 },
            { targets: this.ground, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.8], props: { y: this.ground.offScreenPosition.y }, offset: 1500 },
            { targets: this.peachBuilding, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.peachBuilding.offScreenPosition.y }, offset: 1200 },
            { targets: this.blueBuilding, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.blueBuilding.offScreenPosition.y }, offset: 1100 },
            { targets: this.greenBuilding, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.greenBuilding.offScreenPosition.y }, offset: 1000 },
            { targets: this.treeGrate, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.treeGrate.offScreenPosition.y }, offset: 950 },
            { targets: this.tree, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.tree.offScreenPosition.y }, offset: 900 },
            { targets: this.bench, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.bench.offScreenPosition.y }, offset: 800 },
            { targets: this.trashcan, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.trashcan.offScreenPosition.y }, offset: 850 },
            { targets: this.light0, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.light0.offScreenPosition.y }, offset: 800 },
            { targets: this.light1, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.light1.offScreenPosition.y }, offset: 925 },
            { targets: this.flowers[0], duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.flowers[0].offScreenPosition.y }, offset: 790 },
            { targets: this.flowers[1], duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.flowers[1].offScreenPosition.y }, offset: 780 },
            { targets: this.flowers[2], duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.flowers[2].offScreenPosition.y }, offset: 770 },
            { targets: this.flowers[3], duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.flowers[3].offScreenPosition.y }, offset: 760 },
            { targets: this.flowers[4], duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.flowers[4].offScreenPosition.y }, offset: 750 },
            { targets: this.flowers[5], duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.flowers[5].offScreenPosition.y }, offset: 720 },
            { targets: this.flowers[6], duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.flowers[6].offScreenPosition.y }, offset: 710 },
            { targets: this.flowers[7], duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.flowers[7].offScreenPosition.y }, offset: 700 },
            { targets: this.flowers[8], duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.flowers[8].offScreenPosition.y }, offset: 690 },
            { targets: this.flowers[9], duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.flowers[9].offScreenPosition.y }, offset: 680 },
            { targets: this.flowers[10], duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.flowers[10].offScreenPosition.y }, offset: 670 },
            { targets: this.flowers[11], duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.flowers[11].offScreenPosition.y }, offset: 660 },

            { targets: this.shadows, duration: 750, ease: Phaser.Math.Easing.Quadratic.In, props: { alpha: 0 }, offset: 300 },
            { targets: this.informationContainer, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { x: this.informationContainer.offScreenPosition.x }, offset: 300 },
            { targets: this.menuButton, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { scale: 0 }, offset: 200 },
            { targets: this.progressButton, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { scale: 0 }, offset: 100 },
            {
                targets: [this.treeAndBenchProgress, this.blueBuildingProgress, this.peachBuildingProgress, this.greenBuildingProgress],
                duration: 500,
                ease: Phaser.Math.Easing.Quadratic.In,
                props: { alpha: 0 },
                offset: 0
            }
        ];

        let timeline = this.scene.tweens.timeline({
            tweens: tweens,
        });

        let waitForTimeline = new Promise((resolve) =>
        {
            timeline.on(Phaser.Tweens.Events.TIMELINE_COMPLETE, () => resolve());
        })
        await waitForTimeline;
    }

    /**
     * Send all game objects to their start positions. Some game objects aren't sent "offscreen" but instead set to scale of 0 or alpha of 0.
     */
    _sendTargetsOffScreen()
    {
        super._sendTargetsOffScreen();

        this._sendTargetsOffScreenAll(
            [this.ground, this.blueBuilding, this.greenBuilding, this.peachBuilding, this.light0, this.light1,
            this.bench, this.treeGrate, this.tree, this.trashcan, this.stephen]);
        this._sendTargetsOffScreenAll(this.flowers);

        this.shadows.forEach(shadow => shadow.setAlpha(0));

        this.background.setAlpha(0);
    }

    /**
     * Adds the graphics for the region.
     */
    _addGraphics()
    {
        this.ground = ABCya.Images.addImage(565, 442, UIAssetKeys._07_Downtown_Dntn_Grnd, this);

        this.lightShadow0 = ABCya.Images.addImage(698, 549, UIAssetKeys._07_Downtown_Lt_Shdw, this);
        this.lightShadow1 = ABCya.Images.addImage(432, 235, UIAssetKeys._07_Downtown_Lt_Shdw, this);
        this.blueBuildingShadow = ABCya.Images.addImage(327, 423, UIAssetKeys._07_Downtown_Bldng_Blu_Shdw, this);

        this.peachBuilding = ABCya.Images.addImage(657, 208, UIAssetKeys._08_Downtown_Zoom_In_Peach_Building_PchBldng, this).setInteractive({ useHandCursor: true, pixelPerfect: true, alphaTolerance: 0.5 }).setScale(.625);
        this.blueBuilding = ABCya.Images.addImage(273, 271, UIAssetKeys._08_Downtown_Zoom_In_Blue_Building_BluBldng, this).setInteractive({ useHandCursor: true, pixelPerfect: true, alphaTolerance: 0.5 }).setScale(.5714);
        this.greenBuilding = ABCya.Images.addImage(848, 260, UIAssetKeys._08_Downtown_Zoom_In_Green_Building_GrnBldng, this).setInteractive({ useHandCursor: true, pixelPerfect: true, alphaTolerance: 0.5 }).setScale(.6329);
        this.light0 = ABCya.Images.addImage(697, 475, UIAssetKeys._07_Downtown_Lt, this);
        this.light1 = ABCya.Images.addImage(430, 159, UIAssetKeys._07_Downtown_Lt, this);

        this.add(this.treeAndBenchContainer = this.scene.add.container());
        this.benchShadow = ABCya.Images.addImage(491, 529, UIAssetKeys._07_Downtown_Tree_and_Bench_Area_Bnch_Shdw, this.treeAndBenchContainer);
        this.trashcanShadow = ABCya.Images.addImage(530, 487, UIAssetKeys._07_Downtown_Tree_and_Bench_Area_TrshCan_Shdw, this.treeAndBenchContainer);
        this.treeGrate = ABCya.Images.addImage(423, 464, UIAssetKeys._08_Downtown_Zoom_In_Tree_and_Bench_Gr8, this.treeAndBenchContainer).setScale(.3969);
        this.treeShadow = ABCya.Images.addImage(430, 466, UIAssetKeys._07_Downtown_Tree_and_Bench_Area_Tree_Shdw, this.treeAndBenchContainer);
        this.tree = ABCya.Images.addImage(423, 389, UIAssetKeys._08_Downtown_Zoom_In_Tree_and_Bench_Tree_1, this.treeAndBenchContainer).setScale(.3969);
        this.trashcan = ABCya.Images.addImage(530, 472, UIAssetKeys._08_Downtown_Zoom_In_Tree_and_Bench_Trshcn, this.treeAndBenchContainer).setScale(.3969);
        this.bench = ABCya.Images.addImage(482, 510, UIAssetKeys._08_Downtown_Zoom_In_Tree_and_Bench_Bnch_1, this.treeAndBenchContainer).setScale(.3969);

        this.blueFlowers =
            [
                ABCya.Images.addImage(574, 532, UIAssetKeys._07_Downtown_Tree_and_Bench_Area_Flwr_Blue, this),
                ABCya.Images.addImage(619, 558, UIAssetKeys._07_Downtown_Tree_and_Bench_Area_Flwr_Blue, this),
                ABCya.Images.addImage(558, 591, UIAssetKeys._07_Downtown_Tree_and_Bench_Area_Flwr_Blue, this),
                ABCya.Images.addImage(514, 571, UIAssetKeys._07_Downtown_Tree_and_Bench_Area_Flwr_Blue, this)
            ];
        this.peachFlowers =
            [
                ABCya.Images.addImage(574, 603, UIAssetKeys._07_Downtown_Tree_and_Bench_Area_Flwr_Pch, this),
                ABCya.Images.addImage(612, 572, UIAssetKeys._07_Downtown_Tree_and_Bench_Area_Flwr_Pch, this),
                ABCya.Images.addImage(555, 566, UIAssetKeys._07_Downtown_Tree_and_Bench_Area_Flwr_Pch, this),
                ABCya.Images.addImage(521, 588, UIAssetKeys._07_Downtown_Tree_and_Bench_Area_Flwr_Pch, this),
                ABCya.Images.addImage(608, 532, UIAssetKeys._07_Downtown_Tree_and_Bench_Area_Flwr_Pch, this)
            ];
        this.purpleFlowers =
            [
                ABCya.Images.addImage(601, 556, UIAssetKeys._07_Downtown_Tree_and_Bench_Area_Flwr_Prpl, this),
                ABCya.Images.addImage(658, 567, UIAssetKeys._07_Downtown_Tree_and_Bench_Area_Flwr_Prpl, this),
                ABCya.Images.addImage(544, 603, UIAssetKeys._07_Downtown_Tree_and_Bench_Area_Flwr_Prpl, this)
            ];
        this.flowers = [...this.blueFlowers, ...this.peachFlowers, ...this.purpleFlowers];
        this.treeAndBenchContainer.add(this.treeAndBenchAreaButton = this.scene.add.zone(461, 435, 190, 250).setInteractive({ useHandCursor: true }));
        // to debug where the tree and bench area button area is
        //this.scene.add.rectangle(this.treeAndBenchAreaButton.x, this.treeAndBenchAreaButton.y, this.treeAndBenchAreaButton.width, this.treeAndBenchAreaButton.height, 0xff0000, 0.5);

        this.stephenMask = ABCya.Graphics.addPolygon([{ x: 968, y: 318 }, { x: 968, y: 374 }, { x: 944, y: 387 }, { x: 944, y: 331 }], 0x000000, this).setAlpha(0).createGeometryMask();
        this.stephen = ABCya.Images.addImage(954, 366, UIAssetKeys._07_Downtown_Stephen_Perspective, this).setAlpha(0.25).setScale(0.25);
        this._setAnimationPositions(this.stephen, { x: this.stephen.x, y: this.stephen.y + this.stephen.displayHeight });
        this.stephen.setMask(this.stephenMask);

        this._setAnimationPositionsAll([
            this.ground, this.blueBuilding, this.greenBuilding, this.peachBuilding, this.light0, this.light1, this.bench, this.treeGrate, this.tree, this.trashcan
        ]);

        this._setAnimationPositionsAll(this.flowers);

        this.shadows = [this.benchShadow, this.treeShadow, this.trashcanShadow, this.lightShadow0, this.lightShadow1, this.blueBuildingShadow];
    }

    /**
     * Create the event listeners for the objects that allow the user to zoom into the individual areas.
     */
    _createEventListeners()
    {
        this.blueBuilding.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => this._zoomIntoArea(DowntownRegionStates.BLUE_BUILDING));
        this.greenBuilding.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => this._zoomIntoArea(DowntownRegionStates.GREEN_BUILDING));
        this.peachBuilding.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => this._zoomIntoArea(DowntownRegionStates.PEACH_BUILDING));
        this.treeAndBenchAreaButton.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => this._zoomIntoArea(DowntownRegionStates.TREE_AND_BENCH));
    }

    /**
     * Creates the idle animations. If the idle animations are already playing, it stops it, resets everything, and starts it over.
     */
    _createIdleAnimations()
    {
        if (this.idleAnimations != null && this.idleAnimations.isPlaying())
        {
            this._stopIdleAnimations();
        }

        let tweens = [
            { targets: this.flowers[0], duration: 250, props: { y: "-=2" }, ease: Phaser.Math.Easing.Linear, offset: 250, repeatDelay: 2000, hold: 250 },
            { targets: this.flowers[1], duration: 250, props: { y: "-=2" }, ease: Phaser.Math.Easing.Linear, offset: 250, repeatDelay: 2000, hold: 250 },
            { targets: this.flowers[2], duration: 250, props: { y: "-=2" }, ease: Phaser.Math.Easing.Linear, offset: 0, repeatDelay: 2000, hold: 250 },
            { targets: this.flowers[3], duration: 250, props: { y: "-=2" }, ease: Phaser.Math.Easing.Linear, offset: 0, repeatDelay: 2000, hold: 250 },
            { targets: this.flowers[4], duration: 250, props: { y: "-=2" }, ease: Phaser.Math.Easing.Linear, offset: 0, repeatDelay: 2000, hold: 250 },
            { targets: this.flowers[5], duration: 250, props: { y: "-=2" }, ease: Phaser.Math.Easing.Linear, offset: 250, repeatDelay: 2000, hold: 250 },
            { targets: this.flowers[6], duration: 250, props: { y: "-=2" }, ease: Phaser.Math.Easing.Linear, offset: 0, repeatDelay: 2000, hold: 250 },
            { targets: this.flowers[7], duration: 250, props: { y: "-=2" }, ease: Phaser.Math.Easing.Linear, offset: 0, repeatDelay: 2000, hold: 250 },
            { targets: this.flowers[8], duration: 250, props: { y: "-=2" }, ease: Phaser.Math.Easing.Linear, offset: 250, repeatDelay: 2000, hold: 250 },
            { targets: this.flowers[9], duration: 250, props: { y: "-=2" }, ease: Phaser.Math.Easing.Linear, offset: 250, repeatDelay: 2000, hold: 250 },
            { targets: this.flowers[10], duration: 250, props: { y: "-=2" }, ease: Phaser.Math.Easing.Linear, offset: 250, repeatDelay: 2000, hold: 250 },
            { targets: this.flowers[11], duration: 250, props: { y: "-=2" }, ease: Phaser.Math.Easing.Linear, offset: 0, repeatDelay: 2000, hold: 250 },
            { targets: this.stephen, duration: 2000, props: { y: this.stephen.onScreenPosition.y }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 4000, hold: 6000, repeatDelay: 10000 },
        ];

        this.idleAnimations = this.scene.tweens.timeline({
            tweens: tweens,
            repeat: -1,
            yoyo: true,
        });
    }

    /**
     * Stops the idle animations and resets everything back to their default spots.
     */
    _stopIdleAnimations()
    {
        if (this.idleAnimations == null) return;

        this.idleAnimations.data.forEach((tween) => 
        {
            if (tween.hasStarted)
            {
                tween.data.forEach((data) =>
                {
                    data.target[data.key] = data.start;
                });
            }
        });
        this.idleAnimations.stop();
    }

    /**
     * Call this function to update all the game data associated with this region;
     * This can be used to make the game in sync with the actual game data (after a new game reset for example).
     * Should also be called before entering the region state.
     */
    updateRegionData()
    {
        super.updateRegionData();

        this._updateAreaProgress(this.blueBuildingProgress, getFoundShapesInArea(this.region, AREAS[REGIONS.DOWNTOWN].BLUE_BUILDING));

        this._updateAreaProgress(this.peachBuildingProgress, getFoundShapesInArea(this.region, AREAS[REGIONS.DOWNTOWN].PEACH_BUILDING));

        this._updateAreaProgress(this.greenBuildingProgress, getFoundShapesInArea(this.region, AREAS[REGIONS.DOWNTOWN].GREEN_BUILDING));

        this._updateAreaProgress(this.treeAndBenchProgress, getFoundShapesInArea(this.region, AREAS[REGIONS.DOWNTOWN].TREE_AND_BENCH));
    }

    /**
     * Zooms into the desired area.
     * @param {string} area the AREAS enum value
     */
    _zoomIntoArea(area)
    {
        super._zoomIntoArea(area);

        this._currentState = area;
        this._stopIdleAnimations();

        this.menuButton.disableInteractive();
        this.progressButton.disableInteractive();

        this.blueBuilding.disableInteractive();
        this.peachBuilding.disableInteractive();
        this.greenBuilding.disableInteractive();
        this.treeAndBenchAreaButton.disableInteractive();

        let tweens = [];

        if (this._currentState == DowntownRegionStates.BLUE_BUILDING)
        {
            tweens.push(...this.blueBuildingArea.zoomIn());
        }
        else if (this._currentState == DowntownRegionStates.GREEN_BUILDING)
        {
            tweens.push(...this.greenBuildingArea.zoomIn());
        }
        else if (this._currentState == DowntownRegionStates.TREE_AND_BENCH)
        {
            tweens.push(...this.treeAndBenchArea.zoomIn());
        }
        else if (this._currentState == DowntownRegionStates.PEACH_BUILDING)
        {
            tweens.push(...this.peachBuildingArea.zoomIn());
        }

        tweens.push(
            {
                targets: [
                    this.ground, this.treeAndBenchProgress, this.blueBuildingProgress, this.peachBuildingProgress, this.greenBuildingProgress,
                    this.light0, this.light1, ...this.shadows, ...this.flowers
                ],
                duration: 500,
                props: { alpha: 0 },
                ease: Phaser.Math.Easing.Quadratic.Out,
                offset: 0
            },
            { targets: [this.menuButton, this.progressButton], duration: 500, props: { scale: 0 }, ease: Phaser.Math.Easing.Back.In, offset: 0 },
            { targets: this.informationContainer, duration: 500, props: { x: this.informationContainer.offScreenPosition.x }, ease: Phaser.Math.Easing.Back.In, offset: 0 },
            { targets: this.backButton, duration: 500, props: { scale: 1 }, ease: Phaser.Math.Easing.Back.Out, offset: 500 },
        );

        this.scene.tweens.timeline({
            tweens: tweens,
            onComplete: () =>
            {
                this.backButton.setInteractive();
                if (this._currentState == DowntownRegionStates.BLUE_BUILDING)
                {
                    this.blueBuildingArea.setAreaShapeButtonInteractable(true);
                }
                else if (this._currentState == DowntownRegionStates.GREEN_BUILDING)
                {
                    this.greenBuildingArea.setAreaShapeButtonInteractable(true);
                }
                else if (this._currentState == DowntownRegionStates.TREE_AND_BENCH)
                {
                    this.treeAndBenchArea.setAreaShapeButtonInteractable(true);
                }
                else if (this._currentState == DowntownRegionStates.PEACH_BUILDING)
                {
                    this.peachBuildingArea.setAreaShapeButtonInteractable(true);
                }
            }
        });
    }

    /**
     * Leaves the zoomed in area.
     */
    _leaveZoomedInState()
    {
        super._leaveZoomedInState();

        this.updateRegionData();

        this.backButton.disableInteractive();

        let tweens = [];

        if (this._currentState == DowntownRegionStates.BLUE_BUILDING)
        {
            tweens.push(...this.blueBuildingArea.zoomOut());
        }
        else if (this._currentState == DowntownRegionStates.GREEN_BUILDING)
        {
            tweens.push(...this.greenBuildingArea.zoomOut());
        }
        else if (this._currentState == DowntownRegionStates.TREE_AND_BENCH)
        {
            tweens.push(...this.treeAndBenchArea.zoomOut());
        }
        else if (this._currentState == DowntownRegionStates.PEACH_BUILDING)
        {
            tweens.push(...this.peachBuildingArea.zoomOut());
        }

        tweens.push(
            {
                targets: [
                    this.ground, this.light0, this.light1, ...this.flowers
                ],
                duration: 500,
                props: { alpha: 1 },
                ease: Phaser.Math.Easing.Quadratic.Out,
                offset: 500
            },
            { targets: this.backButton, duration: 500, props: { scale: 0 }, ease: Phaser.Math.Easing.Back.In, offset: 0 },
            { targets: [this.menuButton, this.progressButton], duration: 500, props: { scale: 1 }, ease: Phaser.Math.Easing.Back.Out, offset: 750 },
            { targets: this.informationContainer, duration: 500, props: { x: this.informationContainer.onScreenPosition.x }, ease: Phaser.Math.Easing.Back.Out, offset: 500 },
            { targets: this.shadows, duration: 500, props: { alpha: 1 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 1000 },
        );

        this._createAreaProgressFadeIn(tweens, 1000);

        this.scene.tweens.timeline({
            tweens: tweens,
            onComplete: () =>
            {
                this.progressButton.setInteractive();

                if (this.scene.campRegion.isLocked && gameData.region_unlock_requirements[REGIONS.CAMP] <= getTotalFoundShapes())
                {
                    // unlock the camp regions
                    this.scene.campRegion.isLocked = false;
                    this._playUnlockAnimation();
                }
                else this._checkSpaceRegionLockStatus();

                this.blueBuildingArea.reset();
                this.greenBuildingArea.reset();
                this.peachBuildingArea.reset();
                this.treeAndBenchArea.reset();

                this.menuButton.setInteractive();

                this.blueBuilding.setInteractive();
                this.greenBuilding.setInteractive();
                this.peachBuilding.setInteractive();
                this.treeAndBenchAreaButton.setInteractive();

                this._createIdleAnimations();
            }
        });
    }

    /**
     * Create area progress fade in tweens for areas that have been found.
     * @param {any[]} tweens the current array of tweens
     * @param {number} offset the offset of the progress fade in tweens
     */
    _createAreaProgressFadeIn(tweens, offset)
    {
        if (gameData.saved_data[this.region][AREAS[REGIONS.DOWNTOWN].BLUE_BUILDING].found)
        {
            tweens.push({ targets: this.blueBuildingProgress, duration: 500, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: offset });
        }
        if (gameData.saved_data[this.region][AREAS[REGIONS.DOWNTOWN].TREE_AND_BENCH].found)
        {
            tweens.push({ targets: this.treeAndBenchProgress, duration: 500, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: offset });
        }
        if (gameData.saved_data[this.region][AREAS[REGIONS.DOWNTOWN].GREEN_BUILDING].found)
        {
            tweens.push({ targets: this.greenBuildingProgress, duration: 500, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: offset });
        }
        if (gameData.saved_data[this.region][AREAS[REGIONS.DOWNTOWN].PEACH_BUILDING].found)
        {
            tweens.push({ targets: this.peachBuildingProgress, duration: 500, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: offset });
        }
    }
}

/**
 * The individual area states for the Downtown region.
 */
export const DowntownRegionStates = {
    BLUE_BUILDING: "BlueBuilding",
    PEACH_BUILDING: "PeachBuilding",
    GREEN_BUILDING: "GreenBuilding",
    TREE_AND_BENCH: "Tree&Bench"
}